import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    List,
    Typography,
    Box,
    Button,
    SvgIcon,
} from "@mui/material";
import { BRAND_NAME, MYACCOUNT_URL, URLS, WORDPRESS_URL } from "@constants";
import { useDeviceType } from "@hook/useDeviceType";
import { useHeader } from "./hook/useHeader";
import { NavigationMobile } from "./components/NavigationMobile";
import { useStyle } from "./style";
import logoImg from "@static/img/header/logo.svg";
import profileMobileImg from "@static/img/header/profil-mobile.svg";
import cartImg from "@static/img/header/cart.svg";
import xHeader from "@static/img/header/x-header.svg";
import MenuToggler from "@static/img/header/menu-toggler.svg";
import { BUTTON_IDS } from "@utils/GTM/buttonIds";

interface IParams {
    cartItemsNumber?: number;
    logoutCallback?(): void;
}

export const HeaderMobile = (props: IParams) => {
    const { cartItemsNumber, logoutCallback } = props;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { pathname } = useLocation();
    const { t } = useTranslation("header");
    const { isDesktop } = useDeviceType();
    const { classes } = useStyle({ isDesktop });
    const { handleCartClick, handleLoginLogout } = useHeader({ logoutCallback });

    const handleCloseMenu = useCallback(() => setIsMenuOpen(false), []);

    return (
        <Accordion
            className={classes.mobileAccordion}
            expanded={isMenuOpen}
            onChange={(_, expanded) => setIsMenuOpen(expanded)}
        >
            <AccordionSummary className={classes.accordionSummary} expandIcon={null}>
                <div className={classes.accordionHeader}>
                    <div className={classes.accordionHeaderLeft}>
                        {isMenuOpen ? (
                            <IconButton
                                aria-label={t("closeMenu")}
                                className={`${classes.drawerCloseButton} ${classes.menuToggleButton}`}
                                size="large"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseMenu();
                                }}
                            >
                                <SvgIcon className={classes.closeIcon} component={xHeader} viewBox="0 0 27 27" />
                            </IconButton>
                        ) : (
                            <IconButton
                                aria-label={t("openMenu")}
                                className={`${classes.drawerCloseButton} ${classes.menuToggleButton}`}
                                size="large"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(true);
                                }}
                            >
                                <SvgIcon className={classes.menuIcon} component={MenuToggler} viewBox="0 0 32 25" />
                            </IconButton>
                        )}
                    </div>
                    <div className={classes.accordionHeaderCenter}>
                        <a aria-label={t("homePage")} href={WORDPRESS_URL} id={BUTTON_IDS.TOP_NAV.HOME_PAGE_LOGO}>
                            <SvgIcon
                                className={`${classes.drawerLogo} ${classes.logoMobile}`}
                                component={logoImg}
                                viewBox="0 0 80 46"
                            />
                        </a>
                    </div>
                    <div className={classes.accordionHeaderRight}>
                        <IconButton
                            aria-label={t("profile")}
                            className={classes.drawerCloseButton}
                            size="large"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLoginLogout();
                            }}
                        >
                            <SvgIcon
                                className={classes.profileMobileIcon}
                                component={profileMobileImg}
                                viewBox="0 0 28 28"
                            />
                        </IconButton>
                        {pathname !== `${URLS.CONFIRMATION_PAGE_URL}` && (
                            <Box
                                aria-label={t("cart")}
                                className={classes.cartContainer}
                                id={BUTTON_IDS.TOP_NAV.CART_ICON}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCartClick();
                                }}
                            >
                                <Badge
                                    badgeContent={cartItemsNumber}
                                    className={classes.badge}
                                    invisible={!cartItemsNumber}
                                >
                                    <SvgIcon
                                        className={classes.cartIconMobile}
                                        component={cartImg}
                                        viewBox="0 0 33 30"
                                    />
                                </Badge>
                            </Box>
                        )}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                <List className={classes.drawerMenuList}>
                    <NavigationMobile onLogout={handleLoginLogout} onNavigate={handleCloseMenu} />
                </List>
                <div className={classes.drawerMyAccountButton}>
                    <Button
                        className={classes.myAccountButton}
                        fullWidth
                        href={MYACCOUNT_URL}
                        id={BUTTON_IDS.TOP_NAV.MY_GOMO}
                        variant="contained"
                        onClick={handleCloseMenu}
                    >
                        <Typography className={classes.myAccountButtonText} variant="normal">
                            {t("myAccount", { brand: BRAND_NAME })}
                        </Typography>
                    </Button>
                </div>
            </AccordionDetails>
        </Accordion>
    );
};
